import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import dayjs from 'dayjs';
import TeilnehmerListe from './components/terminDetail/TeilnehmerListe';
import TerminAktionen from './components/terminDetail/TerminAktionen';

const TerminDetailDialog = ({
  detailDialogOpen,
  setDetailDialogOpen,
  selectedTermin,
  handleTeilnahmeStatus,
  getTeilnahmeStatus,
  handleDeleteTermin,
  handleRemoveTeilnahme,
  handleEditTermin,
  nutzer,
  isDateInPast
}) => {
  const navigate = useNavigate();

  if (!selectedTermin) return null;

  const zusagen = selectedTermin.teilnehmer.filter(t => t.status === 'zusage');
  const absagen = selectedTermin.teilnehmer.filter(t => t.status === 'absage');
  const teilnahmeStatus = getTeilnahmeStatus(selectedTermin);
  const istErsteller = selectedTermin.ersteller_id === nutzer.id;
  const kannBearbeiten = istErsteller && !isDateInPast(dayjs(selectedTermin.datum));

  return (
    <Dialog
      open={detailDialogOpen}
      onClose={() => setDetailDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          margin: 1,
          maxHeight: 'calc(100vh - 16px)',
          borderRadius: '20px',
        }
      }}
    >
      {/* Header mit Close Button */}
      <Box sx={{
        bgcolor: '#1f2937',
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
      }}>
        <Typography variant="h6" color="#e0f2fe" sx={{ fontSize: '1.1rem' }}>
          Termin-Details
        </Typography>
        <IconButton
          onClick={() => setDetailDialogOpen(false)}
          size="small"
          sx={{ color: '#93c5fd' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: '#1f2937', p: 2 }}>
        <Stack spacing={2.5}>
          {/* Datum & Zeit Kompakt */}
          <Box sx={{
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '16px',
            p: 2,
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarTodayIcon sx={{ color: '#3b82f6', fontSize: '1.2rem' }} />
                <Typography variant="body1" color="#e0f2fe" sx={{ fontWeight: 500 }}>
                  {new Date(selectedTermin.datum).toLocaleDateString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AccessTimeIcon sx={{ color: '#3b82f6', fontSize: '1.2rem' }} />
                <Typography variant="body1" color="#e0f2fe" sx={{ fontWeight: 500 }}>
                  {selectedTermin.startzeit.substring(0, 5)} Uhr
                </Typography>
              </Box>
              {selectedTermin.notiz && (
                <Typography variant="body2" color="#93c5fd" sx={{ mt: 1, fontStyle: 'italic' }}>
                  "{selectedTermin.notiz}"
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Chat Button */}
          <Button
            variant="outlined"
            startIcon={<CommentIcon />}
            onClick={() => navigate(`/chat/${selectedTermin.id}`)}
            fullWidth
            sx={{
              borderColor: '#3b82f6',
              color: '#e0f2fe',
              borderRadius: '12px',
              py: 1.2,
              textTransform: 'none',
              fontSize: '0.95rem',
              '&:hover': {
                borderColor: '#60a5fa',
                bgcolor: 'rgba(59, 130, 246, 0.05)'
              }
            }}
          >
            Chat öffnen
          </Button>

          {/* Teilnehmer Übersicht */}
          <Stack spacing={2}>
            <TeilnehmerListe
              icon={<ThumbUpIcon sx={{ color: '#22c55e', fontSize: '1.1rem' }} />}
              title="Zusagen"
              teilnehmer={zusagen}
              nutzerId={nutzer.id}
              emptyText="Noch keine Zusagen"
              chipColor="#22c55e"
              headerBorderColor="rgba(34, 197, 94, 0.3)"
              itemBg="rgba(34, 197, 94, 0.05)"
              activeBorderColor="rgba(34, 197, 94, 0.4)"
              avatarBg="#1e3a8a"
              avatarColor="#e0f2fe"
            />
            <TeilnehmerListe
              icon={<ThumbDownIcon sx={{ color: '#ef4444', fontSize: '1.1rem' }} />}
              title="Absagen"
              teilnehmer={absagen}
              nutzerId={nutzer.id}
              emptyText="Keine Absagen"
              chipColor="#ef4444"
              headerBorderColor="rgba(239, 68, 68, 0.3)"
              itemBg="rgba(239, 68, 68, 0.05)"
              activeBorderColor="rgba(239, 68, 68, 0.4)"
              avatarBg="#7f1d1d"
              avatarColor="#fee2e2"
            />
          </Stack>

          {/* Aktionsbuttons */}
          <TerminAktionen
            teilnahmeStatus={teilnahmeStatus}
            terminId={selectedTermin.id}
            onStatus={handleTeilnahmeStatus}
            onRemove={handleRemoveTeilnahme}
          />
        </Stack>
      </DialogContent>

      {/* Footer Aktionen für Ersteller */}
      {kannBearbeiten && (
        <DialogActions sx={{
          bgcolor: '#1f2937',
          p: 2,
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          gap: 1
        }}>
          <IconButton
            onClick={() => {
              handleEditTermin(selectedTermin);
              setDetailDialogOpen(false);
            }}
            sx={{
              color: '#93c5fd',
              bgcolor: 'rgba(59, 130, 246, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.2)'
              }
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteTermin(selectedTermin.id)}
            sx={{
              color: '#f87171',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.2)'
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="#93c5fd">
            Ersteller-Aktionen
          </Typography>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TerminDetailDialog;
